import { Page } from '@common/types';
import { IEnhancedMenuItem } from '@components/table/types';
import { USER_ROLES } from '@core/redux/user/types';

export type AssignedLineRule = 'always' | 'assigned';

/** Production pages where Underwriter uses Admin actions + assigned-line gating. */
export const PRODUCTION_PAGES: Page[] = [
  'Proposals',
  'ProposalDetails',
  'ProposalDetailsGeneric',
  'Policies',
  'GroupPolicies',
  'PolicyDetailsPayments',
  'PolicyDetails',
  'Amendments'
];

/** Pages where assigned-line rules apply for scoped roles. */
export const ASSIGNED_LINE_SCOPED_PAGES: Page[] = [
  ...PRODUCTION_PAGES,
  'Claims'
];

/**
 * Per-action rules. `always` = show without line check (e.g. + New Proposal).
 * `assigned` = requires active assigned line (entity lineId on details, or any on listing).
 */
export const ACTION_LINE_RULES: Partial<
  Record<Page, Record<string, AssignedLineRule>>
> = {
  Proposals: {
    new: 'always'
  },
  Claims: {
    newClaim: 'always'
  }
};

export const ASSIGNED_LINE_ROLES = [
  USER_ROLES.Underwriter,
  USER_ROLES['Claim Officer'],
  USER_ROLES['POS-Quoting'],
  USER_ROLES['POS-Issuance']
] as const;

export function normalizeUserRole(role: string): string {
  switch (role) {
    case 'Claim-Officer':
      return USER_ROLES['Claim Officer'];
    case 'POS-Issuance':
      return USER_ROLES['POS-Issuance'];
    case 'POS-Quoting':
      return USER_ROLES['POS-Quoting'];
    default:
      return role;
  }
}

export function shouldApplyAssignedLineRules(
  userRoles: string[] = []
): boolean {
  const normalized = userRoles.map(normalizeUserRole);

  if (normalized.includes(USER_ROLES.Admin)) {
    return false;
  }

  return normalized.some((role) =>
    (ASSIGNED_LINE_ROLES as readonly string[]).includes(role)
  );
}

export function shouldUseAdminProductionActions(role: string): boolean {
  return normalizeUserRole(role) === USER_ROLES.Underwriter;
}

export function getActiveAssignedLineIds(items: any[] = []): string[] {
  return items
    .filter((item) => String(item?.status ?? '').toLowerCase() === 'active')
    .map((item) => item?.lineID ?? item?.line?.id)
    .filter(Boolean);
}

export function isUserAssignedToLine(
  lineId: string | undefined,
  lineAlternateId: string | undefined,
  assignedLineIds: string[],
  isBackoffice: boolean
): boolean {
  if (!isBackoffice) {
    return true;
  }

  if (!assignedLineIds.length) {
    return false;
  }

  if (!lineId && !lineAlternateId) {
    return true;
  }

  return assignedLineIds.some(
    (id) => id === lineId || (lineAlternateId ? id === lineAlternateId : false)
  );
}

function getActionRule(page: Page, actionName: string): AssignedLineRule {
  return ACTION_LINE_RULES[page]?.[actionName] ?? 'assigned';
}

export function getActionAssignedLineRule(
  page: Page,
  actionName: string
): AssignedLineRule {
  return getActionRule(page, actionName);
}

function isActionAllowedForAssignedLines(
  rule: AssignedLineRule,
  assignedLineIds: string[],
  lineId?: string,
  lineAlternateId?: string
): boolean {
  if (rule === 'always') {
    return true;
  }

  if (!assignedLineIds.length) {
    return false;
  }

  if (lineId || lineAlternateId) {
    return assignedLineIds.some(
      (id) =>
        (lineId && id === lineId) ||
        (lineAlternateId && id === lineAlternateId)
    );
  }

  return true;
}

export function applyAssignedLineRulesToActions(
  actions: IEnhancedMenuItem[],
  page: Page,
  assignedLineIds: string[],
  lineId?: string,
  applyRules = false,
  lineAlternateId?: string
): IEnhancedMenuItem[] {
  if (!applyRules) {
    return actions;
  }

  return actions.map((action) => {
    if (action.hidden) {
      return action;
    }

    const rule = getActionRule(page, action.name);
    const allowed = isActionAllowedForAssignedLines(
      rule,
      assignedLineIds,
      lineId,
      lineAlternateId
    );

    if (allowed) {
      return action;
    }

    return {
      ...action,
      hidden: true,
      disabled: true
    };
  });
}
